// src/services/mobileCrewService.ts
// ─────────────────────────────────────────────────────────────────
// Mobile Crew Service — Phase 1 (Mock)
// Wraps MOCK_ROSTER_NOTIFICATIONS from mockDataMobile.ts.
// At beta: replace bodies with fetch('/api/crew/...')
// ─────────────────────────────────────────────────────────────────

import {
  MOCK_ROSTER_NOTIFICATIONS,
  ATTENDANCE_META,
  type RosterNotification,
  type AttendanceStatus,
} from '../utils/mockDataMobile'
import { getRosterAllocations } from '../pages/Roster'
import { MOCK_JOBS }            from '../utils/mockData'
import type { CrewMember, EmergencyContact } from '../types/mobileCrewTypes'
import { getUnreadCount as _count } from '../utils/mockDataNotificationBus'

// ── Mock emergency contacts (to be added to MOCK_EMPLOYEES at Phase 2) ──
const MOCK_EMERGENCY_CONTACTS: Record<string, EmergencyContact> = {
  e1: { name: 'Maria Rivera',    relationship: 'Wife',       phone: '0412 111 222' },
  e2: { name: 'Thomas Grant',    relationship: 'Husband',    phone: '0413 222 333' },
  e3: { name: 'Anh Nguyen',      relationship: 'Sister',     phone: '0414 333 444' },
  e4: { name: 'Sharon Walsh',    relationship: 'Wife',       phone: '0415 444 555' },
  e5: { name: 'Raj Patel',       relationship: 'Husband',    phone: '0416 555 666' },
  e6: { name: 'Gary Fielding',   relationship: 'Father',     phone: '0417 666 777' },
  e7: { name: 'Elena Torres',    relationship: 'Wife',       phone: '0418 777 888' },
  e8: { name: 'James Kim',       relationship: 'Husband',    phone: '0419 888 999' },
}

// Local store for status changes within a session
let _statusOverrides: Record<string, AttendanceStatus> = {}

function toCrewMember(n: RosterNotification): CrewMember {
  return {
    id:                   n.id,
    employeeId:           n.employeeId,
    employeeName:         n.employeeName,
    jobId:                n.jobId,
    jobNumber:            n.jobNumber,
    jobName:              n.jobName,
    siteAddress:          (MOCK_JOBS as any[]).find(j => j.id === n.jobId)?.siteAddress ?? '',
    date:                 n.date,
    role:                 n.role,
    shiftType:            (() => {
      // Phase 2 fix: look up actual shift type from the shared allocation store
      const allocs = getRosterAllocations()
      const alloc  = allocs.find(a => a.employeeId === n.employeeId && a.date === n.date)
      return alloc?.shiftType ?? 'DAY'
    })(),
    supervisorName:       n.supervisorName,
    notes:                n.notes,
    pushedAt:             n.pushedAt,
    pushedByName:         n.pushedByName,
    status:               _statusOverrides[n.id] ?? n.status as CrewMember['status'],
    confirmedAt:          n.confirmedAt,
    rejectedAt:           n.rejectedAt,
    rejectionCode:        n.rejectionCode,
    rejectionNote:        n.rejectionNote,
    startTime:            n.startTime,
    finishTime:           n.finishTime,
    hoursWorked:          n.hoursWorked,
    emergencyContact:     MOCK_EMERGENCY_CONTACTS[n.employeeId] ?? null,
    unreadNotificationCount: _count(n.employeeId),
    lastSyncTimestamp:    n.confirmedAt ?? n.pushedAt,
    recallStatus:         n.recalledAt ? 'RECALLED' : 'NONE',
  }
}

/** All crew currently in the push log, optionally filtered by job. */
export async function getOnSiteCrew(jobId?: string): Promise<CrewMember[]> {
  // Phase 6: GET /api/crew/on-site?jobId=...
  const records = jobId
    ? MOCK_ROSTER_NOTIFICATIONS.filter(n => n.jobId === jobId)
    : MOCK_ROSTER_NOTIFICATIONS
  return Promise.resolve(records.map(toCrewMember))
}

/** Crew for a specific job — alias used by Live Crew job filter. */
export async function getCrewByJob(jobId: string): Promise<CrewMember[]> {
  return getOnSiteCrew(jobId)
}

/** Update attendance status in the session store (simulates field confirmation). */
export async function updateAttendanceStatus(
  recordId: string,
  newStatus: AttendanceStatus,
): Promise<boolean> {
  // Phase 6: PATCH /api/crew/attendance/:recordId
  _statusOverrides[recordId] = newStatus
  return Promise.resolve(true)
}

/** Emergency contact for a single employee — used in Live Crew one-tap-to-call. */
export async function getEmergencyContacts(
  employeeId: string,
): Promise<EmergencyContact | null> {
  // Phase 6: GET /api/employees/:id/emergency-contact
  return Promise.resolve(MOCK_EMERGENCY_CONTACTS[employeeId] ?? null)
}

/** Sync summary for the page header badges. */
export async function getMobileSyncSummary() {
  // Phase 6: GET /api/crew/sync-summary
  const { MOBILE_SYNC_SUMMARY } = await import('../utils/mockDataMobile')
  return Promise.resolve({
    ...MOBILE_SYNC_SUMMARY,
    unreadNotifications: _count(),
  })
}

/** Attendance status metadata (labels, colours) — no API call needed. */
export { ATTENDANCE_META }
